<template>
  <!-- One button per host language. It writes the host locale the same way
       LanguagePicker does (setHostLocale → MetaShipGlobalState), so the shell
       and every hosted module follow it. -->
  <div class="si-lang" role="group" :aria-label="t('account.language')">
    <svg class="si-lang__globe" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </svg>
    <!-- Flag only. The native name is the aria-label, and a tooltip shows it
         on hover and on keyboard focus (touch has no hover; the label stays). -->
    <v-tooltip
      v-for="lang in LANGUAGE_OPTIONS"
      :key="lang.code"
      :text="lang.label"
      location="top"
      open-on-hover
      open-on-focus
    >
      <template #activator="{ props: tip }">
        <button
          v-bind="tip"
          type="button"
          class="si-lang__option"
          :class="{ 'si-lang__option--active': lang.code === locale }"
          :data-test="`lang-${lang.code}`"
          :aria-label="lang.label"
          :aria-pressed="lang.code === locale ? 'true' : 'false'"
          @click="setHostLocale(lang.code)"
        >
          <img
            class="si-lang__flag"
            :src="lang.flag"
            alt=""
            width="20"
            height="15"
            referrerpolicy="no-referrer"
          />
        </button>
      </template>
    </v-tooltip>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import { useLocale } from "@i18n/vue3";
import { LANGUAGE_OPTIONS, setHostLocale } from "@/i18n/languages";

export default defineComponent({
  name: "LanguageSwitch",
  setup() {
    const { t } = useI18n();
    const locale = useLocale();
    return { t, locale, LANGUAGE_OPTIONS, setHostLocale };
  },
});
</script>

<style scoped>
.si-lang {
  align-self: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 4px;
  border-radius: 24px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid var(--sh-hairline);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}
.si-lang__globe {
  width: 16px;
  height: 16px;
  margin: 0 4px 0 8px;
  fill: none;
  stroke: var(--sh-text-60);
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.si-lang__option {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  padding: 0 12px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--sh-text-60);
  cursor: pointer;
  transition: background 0.15s ease;
}
.si-lang__option:hover {
  background: var(--sh-hover);
}
.si-lang__option:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}
.si-lang__option--active,
.si-lang__option--active:hover {
  background: rgb(var(--v-theme-primary));
}
.si-lang__tip {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translate(-50%, 4px);
  white-space: nowrap;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgb(var(--v-theme-on-surface));
  color: rgb(var(--v-theme-surface));
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
  z-index: 2;
}
.si-lang__tip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border: 5px solid transparent;
  border-top-color: rgb(var(--v-theme-on-surface));
}
.si-lang__option:hover .si-lang__tip,
.si-lang__option:focus-visible .si-lang__tip {
  opacity: 1;
  transform: translate(-50%, 0);
}
@media (prefers-reduced-motion: reduce) {
  .si-lang__tip {
    transition: none;
  }
}
.si-lang__flag {
  width: 20px;
  height: 15px;
  border-radius: 3px;
  object-fit: cover;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12);
}
</style>
